import { useEffect, useState, useMemo } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { Formik, Form } from 'formik';
import {
    Button,
    Box,
    Heading,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    SimpleGrid,
    Text,
} from '@chakra-ui/react';

import useCommonToast from '../../../../hooks/useCommonToast';
import useItemCreationWizard from '../../../../hooks/useItemCreationWizard';

import InputFormikField from 'components/forms/InputFormikField';
import ComboboxFormikField from 'components/forms/ComboboxFormikField';
import MultiSelectField from 'components/forms/MultiSelectField';
import CheckboxField from '../../../forms/CheckboxField';
import DefinitionsSkeleton from '../../../utils/DefinitionsSkeleton';
import SearchBar from '../../../utils/SearchBar';

import { isSubmitDisabled } from '../../../../services/utils';
import { getWeatherStations, submitWeatherZone } from 'services/itemCreationWizard';

const AddWeatherZoneModal = ({ weatherZones = [], ...rest }) => {
    const intl = useIntl();
    const { toast } = useCommonToast();
    const varsToSimDefault = 'minDB maxDB';
    const stationWeightDefault = 1;

    const [isLoading, setIsLoading] = useState(true);
    const [displayStationCallSign, setDisplayStationCallSign] = useState(false);
    const [weatherStations, setWeatherStations] = useState([]);
    const [moduleProcesses, setModuleProcesses] = useState([]);
    const [selectedWeatherStations, setSelectedWeatherStations] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');

    const { updateAvailableWeatherZones } = useItemCreationWizard();

    useEffect(() => {
        if (!Array.isArray(weatherStations) || weatherStations.length === 0) {
            getWeatherStations().then((response) => {
                setWeatherStations(response.weatherStations);
                setModuleProcesses(response.moduleProcesses);
            });
        } else {
            setIsLoading(false);
        }
    }, [weatherStations]);

    const initialFormValues = {
        name: '',
        processFlowName: '',
        varsToSim: varsToSimDefault,
        weatherStations: [],
        callSign: false,
        search: '',
    };

    const formLabels = {
        name: intl.formatMessage({ id: 'item_creation_wizard_weatherZones_name_label' }),
        processFlowName: intl.formatMessage({ id: 'item_creation_wizard_weatherZones_process_label' }),
        varsToSim: intl.formatMessage({ id: 'item_creation_wizard_weatherZones_varsToSim_label' }),
        weatherStations: intl.formatMessage({ id: 'item_creation_wizard_weatherZones_stations_label' }),
        callSign: intl.formatMessage({ id: 'item_creation_wizard_weatherZones_callSign_label' }),
        search: intl.formatMessage({ id: 'common_search' }),
    };

    const toggleStationDisplay = (value) => {
        setDisplayStationCallSign(value);
    };

    const onStationSelectionChange = (selectedStations) => {
        setSelectedWeatherStations(selectedStations);
    };

    const filteredStations = useMemo(() => {
        const term = searchTerm.trimStart().toUpperCase();

        if (!displayStationCallSign && term.length < 2) {
            return weatherStations;
        } else if (displayStationCallSign && term.length < 1) {
            return weatherStations;
        }

        return weatherStations.filter((station) => {
            if (displayStationCallSign) {
                const isCallSignMatch = station.callSign?.toUpperCase().includes(term) ?? false;
                return isCallSignMatch;
            } else {
                const isNameMatch = station.name?.toUpperCase().includes(term) ?? false;
                return isNameMatch;
            }
        });
    }, [weatherStations, searchTerm, displayStationCallSign]);

    const validateName = (value) => {
        const trimmed = value.trim();

        //required check
        if (!trimmed) {
            return intl.formatMessage({ id: 'common_forms_validation_required' }, { label: formLabels.name });
        }

        //length check
        if (trimmed.length > 50) {
            return intl.formatMessage(
                { id: 'common_forms_validation_length' },
                { label: formLabels.name, lengthRule: '50 characters or less' }
            );
        }

        //unique name check
        const nameExists = weatherZones.some((zone) => zone.name.toLowerCase() === trimmed.toLowerCase());

        if (nameExists) {
            return intl.formatMessage({ id: 'common_already_exists_error' }, { itemType: 'Weather Zone' });
        }
    };

    const onFormSubmit = async (values) => {
        const { name, moduleProcessId } = values;
        const weatherStationIds = selectedWeatherStations;

        const weatherZonePayload = {
            Name: name,
            ModuleProcessId: moduleProcessId,
            VarsToSim: varsToSimDefault,
            WeatherStationIds: weatherStationIds,
            StationWeight: stationWeightDefault,
        };

        await submitWeatherZone(weatherZonePayload).then(() => {});

        const successMessage = intl.formatMessage({ id: 'common_creation_success' }, { item: 'weather zone' });
        toast(successMessage);

        await updateAvailableWeatherZones();

        rest.onClose();
    };

    return (
        <Modal {...rest}>
            <ModalOverlay />

            <ModalContent pt={3}>
                <ModalHeader pr={12}>
                    <Heading as="h3" variant="h3" textTransform="capitalize">
                        <FormattedMessage id="item_creation_wizard_weatherZone_heading" />
                    </Heading>
                </ModalHeader>
                <ModalCloseButton mt={4} mr={3} h={6} w={6} />

                {isLoading ? (
                    <ModalBody>
                        <DefinitionsSkeleton />
                    </ModalBody>
                ) : (
                    <Formik enableReinitialize initialValues={initialFormValues} onSubmit={onFormSubmit}>
                        {({ values, isSubmitting, errors, touched }) => (
                            <Form>
                                <ModalBody borderBottom="1px" borderColor="gray.200">
                                    <SimpleGrid columns={{ xl: 3 }} spacingX={6} spacingY={4} mb={4}>
                                        <InputFormikField
                                            validate={(value) => validateName(value)}
                                            id="name"
                                            name="name"
                                            type="text"
                                            label={formLabels.name}
                                            isRequired
                                        />

                                        <ComboboxFormikField
                                            id="moduleProcessId"
                                            name="moduleProcessId"
                                            label={formLabels.processFlowName}
                                            options={moduleProcesses}
                                            valueKey="id"
                                            labelKey="description"
                                            placeholderValue=""
                                            placeholderLabel={formLabels.selectPlaceholder}
                                        />

                                        <InputFormikField
                                            id="varsToSim"
                                            name="varsToSim"
                                            type="text"
                                            label={formLabels.varsToSim}
                                            isRequired
                                            isDisabled={true}
                                        />

                                        <Box mt={0}>
                                            <Text color="gray.600" fontWeight={500} fontSize={12} pb={1} opacity={1}>
                                                {displayStationCallSign ? (
                                                    <FormattedMessage id="item_creation_wizard_weatherStation_search_callSign" />
                                                ) : (
                                                    <FormattedMessage id="item_creation_wizard_weatherStation_search_name" />
                                                )}
                                            </Text>
                                            <SearchBar
                                                textHolder={intl.formatMessage({
                                                    id: 'item_creation_wizard_weatherStation_search',
                                                })}
                                                onChange={(value) => setSearchTerm(value)}
                                                searchTerm={searchTerm}
                                            />
                                        </Box>

                                        <MultiSelectField
                                            id="multiSelectWeatherStations"
                                            name="multiSelectWeatherStations"
                                            label={formLabels.weatherStations}
                                            placeholderLabel={intl.formatMessage({
                                                id: 'run_a_study_select_placeholder',
                                            })}
                                            multipleSelectedLabel={intl.formatMessage({
                                                id: 'run_a_study_multiple_selected_placeholder',
                                            })}
                                            allSelectedLabel={intl.formatMessage({
                                                id: 'common_all_selected_label',
                                            })}
                                            options={filteredStations}
                                            valueKey="id"
                                            labelKey={displayStationCallSign ? 'callSign' : 'name'}
                                            onChange={onStationSelectionChange}
                                            value={selectedWeatherStations}
                                            isRequired
                                        />

                                        <CheckboxField
                                            my={7}
                                            id="toggleDisplay"
                                            name="toggleDisplay"
                                            onChange={(e) => toggleStationDisplay(e.target.checked)}
                                            isChecked={displayStationCallSign}
                                        >
                                            <Text size="sm">{formLabels.callSign}</Text>
                                        </CheckboxField>
                                    </SimpleGrid>
                                </ModalBody>

                                <ModalFooter justifyContent="stretch" flexWrap="wrap">
                                    <Box ml="auto">
                                        <Button variant="secondary" onClick={rest.onClose}>
                                            <Box as="span" textTransform="capitalize">
                                                <FormattedMessage id="common_cancel" />
                                            </Box>
                                        </Button>

                                        <Button
                                            type="submit"
                                            isDisabled={
                                                isSubmitDisabled({ errors, touched }) ||
                                                selectedWeatherStations.length === 0
                                            }
                                            isLoading={isSubmitting}
                                            ml={3}
                                        >
                                            <FormattedMessage id="common_item_create_btn" />
                                        </Button>
                                    </Box>
                                </ModalFooter>
                            </Form>
                        )}
                    </Formik>
                )}
            </ModalContent>
        </Modal>
    );
};

export default AddWeatherZoneModal;
